import { db, functions } from "@/firebase";

const userRoleRef = db.collection("userRole");
const userProfileRef = db.collection("userProfile");
const membershipSyncInFlight = new Map();
const membershipSyncCache = new Map();
const likedSectionsInFlight = new Map();
const likedSectionsCache = new Map();
const isDev = process.env.NODE_ENV !== "production";
const likedSectionsMetrics = {
  cacheHits: 0,
  inFlightHits: 0,
  firestoreReads: 0,
};
const defaultUiPreferences = {
  likedSectionsTableRegionsExpandedByDefault: true,
  sectionDataTableRegionsExpandedByDefault: true,
  partnerCarouselExpandedByDefault: true,
};
const defaultUserFilters = {
  regions: [],
  levels: [],
  classes: [],
  isLiked: false,
  freeAccessOnly: false,
  dangersOnly: false,
};

function normalizeUserUiPreferences(preferences = {}) {
  return {
    likedSectionsTableRegionsExpandedByDefault:
      typeof preferences.likedSectionsTableRegionsExpandedByDefault === "boolean"
        ? preferences.likedSectionsTableRegionsExpandedByDefault
        : defaultUiPreferences.likedSectionsTableRegionsExpandedByDefault,
    sectionDataTableRegionsExpandedByDefault:
      typeof preferences.sectionDataTableRegionsExpandedByDefault === "boolean"
        ? preferences.sectionDataTableRegionsExpandedByDefault
        : defaultUiPreferences.sectionDataTableRegionsExpandedByDefault,
    partnerCarouselExpandedByDefault:
      typeof preferences.partnerCarouselExpandedByDefault === "boolean"
        ? preferences.partnerCarouselExpandedByDefault
        : defaultUiPreferences.partnerCarouselExpandedByDefault,
  };
}

function normalizeUserFilters(filters = {}) {
  return {
    regions: Array.isArray(filters.regions) ? filters.regions : [],
    levels: Array.isArray(filters.levels) ? filters.levels : [],
    classes: Array.isArray(filters.classes) ? filters.classes : [],
    isLiked: typeof filters.isLiked === "boolean" ? filters.isLiked : false,
    freeAccessOnly: typeof filters.freeAccessOnly === "boolean" ? filters.freeAccessOnly : false,
    dangersOnly: typeof filters.dangersOnly === "boolean" ? filters.dangersOnly : false,
  };
}

function parseUserFilters(filtersValue) {
  if (!filtersValue) {
    return { ...defaultUserFilters };
  }

  if (typeof filtersValue === "object") {
    return normalizeUserFilters(filtersValue);
  }

  if (typeof filtersValue !== "string") {
    return { ...defaultUserFilters };
  }

  try {
    return normalizeUserFilters(JSON.parse(filtersValue));
  } catch (error) {
    console.warn("Invalid user filters JSON, using defaults:", error);
    return { ...defaultUserFilters };
  }
}

function logLikedSectionsMetric(type, uid) {
  if (!isDev) {
    return;
  }
  console.debug(
    `[UserService:getLikedSections] ${type} uid=${uid} cacheHits=${likedSectionsMetrics.cacheHits} inFlightHits=${likedSectionsMetrics.inFlightHits} firestoreReads=${likedSectionsMetrics.firestoreReads}`,
  );
}

function createQuerySnapshotLike(docs) {
  return {
    docs: docs.map((doc) => ({
      id: doc.id,
      data: () => ({ ...doc.data }),
    })),
  };
}

export const UserService = {
  state: {
    user: [],
  },
  actions: {
    async getUserProfileByUid(uid) {
      const userProfileDoc = userProfileRef.doc(uid);
      const userProfileSnap = await userProfileDoc.get();

      if (userProfileSnap.exists) {
        return userProfileSnap.data();
      } else {
        console.log("Not matching user profile");
        return null;
      }
    },
    async getUserFiltersByUid(uid) {
      const userProfileDoc = userProfileRef.doc(uid);
      const userProfileSnap = await userProfileDoc.get();

      if (userProfileSnap.exists) {
        const userProfile = userProfileSnap.data();
        return parseUserFilters(userProfile?.filters);
      } else {
        return { ...defaultUserFilters };
      }
    },
    async setUserProfile(uid, userProfile) {
      const userProfileDoc = userProfileRef.doc(uid);
      const res = userProfileDoc.set(
        {
          filters: userProfile.filters,
          likedSections: userProfile.likedSections,
        },
        { merge: true },
      );

      return res;
    },
    async setUserFilters(uid, userFilters) {
      const userProfileDoc = userProfileRef.doc(uid);
      const res = userProfileDoc.set(
        {
          filters: JSON.stringify(userFilters),
        },
        { merge: true },
      );

      return res;
    },
    async getUserUiPreferencesByUid(uid) {
      if (!uid) {
        return { ...defaultUiPreferences };
      }

      const userProfileDoc = userProfileRef.doc(uid);
      const userProfileSnap = await userProfileDoc.get();

      if (!userProfileSnap.exists) {
        return { ...defaultUiPreferences };
      }

      const userProfile = userProfileSnap.data() || {};
      return normalizeUserUiPreferences(userProfile.uiPreferences || {});
    },
    async setUserUiPreferences(uid, uiPreferences) {
      const userProfileDoc = userProfileRef.doc(uid);
      const normalizedPreferences = normalizeUserUiPreferences(uiPreferences);

      return userProfileDoc.set(
        {
          uiPreferences: normalizedPreferences,
        },
        { merge: true },
      );
    },
    async getUserRoleByUid(uid) {
      const userRoleDoc = userRoleRef.doc(uid);
      const userRoleSnap = await userRoleDoc.get();

      if (userRoleSnap.exists) {
        return userRoleSnap.data();
      } else {
        console.log("Not matching user role");
        const userMembershipInfo = { id: uid, access: "aucun", endDate: null, note: "" };
        const setUserMembership = functions.httpsCallable("setUserMembership");
        const userRole = await setUserMembership({ userRole: userMembershipInfo })
          .then((result) => {
            return result;
          })
          .catch((error) => {
            console.error("Error setting user membership:", error);
            return null;
          });

        return userRole.data;
      }
    },
    async getUserRoles(uid) {
      var isAdmin = false;
      var isMember = false;

      const userRole = await this.getUserRoleByUid(uid);
      if (userRole) {
        if (userRole.access === "admin") {
          isAdmin = true;
          isMember = true;
        } else if (userRole.access === "member") {
          var todayMinusOne = new Date();
          todayMinusOne.setDate(todayMinusOne.getDate() - 1);

          var membershipEndDate = userRole.endDate;

          if (membershipEndDate > todayMinusOne.getTime() / 1000) {
            isMember = true;
          }
        }
      }
      return { isAdmin: isAdmin, isMember: isMember, membershipEndDate: userRole ? userRole.endDate : null };
    },
    async isMembershipActive(uid) {
      const userRole = await this.getUserRoleByUid(uid);
      if (userRole) {
        if (userRole.access === "admin") {
          return true;
        } else if (userRole.access === "member") {
          var todayMinusOne = new Date();
          todayMinusOne.setDate(todayMinusOne.getDate() - 1);

          var membershipEndDate = userRole.endDate;

          if (!membershipEndDate) {
            return false;
          }
          if (membershipEndDate > todayMinusOne.getTime() / 1000) {
            return true;
          }
        }
      } else {
        return false;
      }
      return false;
    },
    async fetchCustomerSubscriptions() {
      const getActiveSubscription = functions.httpsCallable("stripeGetActiveSubscription");
      const { data } = await getActiveSubscription();
      return data.subscription;
    },
    async likeSection(uid, sectionId) {
      const likedSectionDoc = userProfileRef.doc(uid).collection("likedSections").doc(sectionId);
      await likedSectionDoc.set({ createdAt: new Date() });

      const cached = likedSectionsCache.get(uid);
      if (cached) {
        const hasEntry = cached.docs.some((doc) => doc.id === sectionId);
        if (!hasEntry) {
          cached.docs.push({ id: sectionId, data: { createdAt: new Date() } });
        }
        cached.timestamp = Date.now();
      }
    },

    async unlikeSection(uid, sectionId) {
      const likedSectionDoc = userProfileRef.doc(uid).collection("likedSections").doc(sectionId);
      await likedSectionDoc.delete();

      const cached = likedSectionsCache.get(uid);
      if (cached) {
        cached.docs = cached.docs.filter((doc) => doc.id !== sectionId);
        cached.timestamp = Date.now();
      }
    },
    async getLikedSections(uid) {
      if (!uid) {
        return createQuerySnapshotLike([]);
      }

      const now = Date.now();
      const cached = likedSectionsCache.get(uid);
      const cacheTtlMs = 60000;

      if (cached && now - cached.timestamp < cacheTtlMs) {
        likedSectionsMetrics.cacheHits += 1;
        logLikedSectionsMetric("cache-hit", uid);
        return createQuerySnapshotLike(cached.docs);
      }

      if (likedSectionsInFlight.has(uid)) {
        likedSectionsMetrics.inFlightHits += 1;
        logLikedSectionsMetric("in-flight-hit", uid);
        return likedSectionsInFlight.get(uid);
      }

      likedSectionsMetrics.firestoreReads += 1;
      logLikedSectionsMetric("firestore-read", uid);

      const queryPromise = userProfileRef
        .doc(uid)
        .collection("likedSections")
        .get()
        .then((snapshot) => {
          const docs = snapshot.docs.map((doc) => ({ id: doc.id, data: doc.data() }));
          likedSectionsCache.set(uid, { docs, timestamp: Date.now() });
          return createQuerySnapshotLike(docs);
        })
        .finally(() => {
          likedSectionsInFlight.delete(uid);
        });

      likedSectionsInFlight.set(uid, queryPromise);
      return queryPromise;
    },
    getLikedSectionsMetrics() {
      return { ...likedSectionsMetrics };
    },
    resetLikedSectionsMetrics() {
      likedSectionsMetrics.cacheHits = 0;
      likedSectionsMetrics.inFlightHits = 0;
      likedSectionsMetrics.firestoreReads = 0;
    },
    resetLikedSectionsCache(uid = null) {
      if (uid) {
        likedSectionsCache.delete(uid);
        likedSectionsInFlight.delete(uid);
        return;
      }

      likedSectionsCache.clear();
      likedSectionsInFlight.clear();
    },
    async syncMembershipForUser(user) {
      if (!user) return { isMember: false };
      const cacheKey = user.uid;
      const now = Date.now();
      const cachedSync = membershipSyncCache.get(cacheKey);

      // Avoid duplicate calls fired in quick succession by multiple auth callbacks.
      if (cachedSync && now - cachedSync.timestamp < 30000) {
        return cachedSync.result;
      }

      if (membershipSyncInFlight.has(cacheKey)) {
        return membershipSyncInFlight.get(cacheKey);
      }

      console.log("Syncing membership for user:", user.uid);

      const syncPromise = (async () => {
        try {
          const stripeFindCustomerActiveTransactionsByEmail = functions.httpsCallable(
            "stripeFindCustomerActiveTransactionsByEmail",
          );

          const customerActiveTransactionRaw = await stripeFindCustomerActiveTransactionsByEmail({
            uId: user.uid,
            email: user.email,
          })
            .then((result) => result.data)
            .catch((error) => {
              console.error("syncMembershipForUser: cloud function error", error);
              return { isMember: false, endDate: null };
            });

          const normalizeEndDate = (value) => {
            const numeric = Number(value);
            if (Number.isFinite(numeric) && numeric > 0) {
              return numeric;
            }

            if (typeof value === "string") {
              const parsed = Date.parse(value);
              if (Number.isFinite(parsed)) {
                return Math.floor(parsed / 1000);
              }
            }

            return null;
          };

          let customerActiveTransaction = customerActiveTransactionRaw;
          const activeTransactionsByEmail = customerActiveTransactionRaw?.ActiveTransactionsbyemail;

          if (Array.isArray(activeTransactionsByEmail) && activeTransactionsByEmail.length > 0) {
            let selectedTransaction = null;
            let latestEndDate = null;

            for (const transaction of activeTransactionsByEmail) {
              if (!transaction) {
                continue;
              }

              const transactionEndDate = normalizeEndDate(
                transaction.endDate ?? transaction.end_date ?? transaction.current_period_end,
              );

              if (!transactionEndDate) {
                continue;
              }

              if (latestEndDate === null || transactionEndDate > latestEndDate) {
                latestEndDate = transactionEndDate;
                selectedTransaction = transaction;
              }
            }

            if (latestEndDate !== null) {
              customerActiveTransaction = {
                ...customerActiveTransactionRaw,
                ...selectedTransaction,
                isMember: true,
                endDate: latestEndDate,
              };
            }
          }

          console.log("Customer active transaction:", customerActiveTransaction);

          // The cloud function records the membership in userRole itself; clients cannot write their own role.
          if (customerActiveTransaction && customerActiveTransaction.isMember && customerActiveTransaction.endDate) {
            const result = { isMember: true, endDate: customerActiveTransaction.endDate };
            membershipSyncCache.set(cacheKey, { timestamp: Date.now(), result });
            return result;
          }
        } catch (e) {
          console.error("syncMembershipForUser failed", e);
        }
        const result = { isMember: false };
        membershipSyncCache.set(cacheKey, { timestamp: Date.now(), result });
        return result;
      })();

      membershipSyncInFlight.set(cacheKey, syncPromise);

      try {
        return await syncPromise;
      } finally {
        membershipSyncInFlight.delete(cacheKey);
      }
    },
  },
};
